import React, { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { HardDrive, Trash2, AlertTriangle, FileText, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value, sub, testId }) => (
  <div className="bg-surface border border-border rounded-xl shadow-sm p-6">
    <div className="text-[11px] uppercase tracking-widest text-text-muted mb-2">{label}</div>
    <div data-testid={testId} className="text-3xl tracking-tight font-medium text-text-primary">{value}</div>
    {sub && <div className="text-xs text-text-muted mt-1">{sub}</div>}
  </div>
);

export default function AdminStorage() {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [beforeDate, setBeforeDate] = useState("");
  const [step1Open, setStep1Open] = useState(false);
  const [step2Open, setStep2Open] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [failed, setFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const [{ data: storage }, { data: cleanupLogs }] = await Promise.all([
        api.get("/admin/storage"),
        api.get("/admin/storage/logs"),
      ]);
      setData(storage);
      setLogs(cleanupLogs);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filesPreviewCount = data && beforeDate
    ? data.files.filter(f => (f.request_created_at || f.modified_at) < beforeDate).length
    : 0;

  const openStep1 = () => {
    if (!beforeDate) { toast.error("Seleziona una data"); return; }
    setStep1Open(true);
  };

  const proceedToStep2 = () => {
    setStep1Open(false);
    setAcknowledged(false);
    setStep2Open(true);
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const { data: res } = await api.delete("/admin/storage", {
        params: { before_date: beforeDate, confirm: true },
      });
      toast.success(`Eliminati ${res.files_deleted} PDF (${res.mb_freed} MB liberati)`);
      setStep2Open(false);
      setBeforeDate("");
      load();
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl tracking-tight font-medium text-text-primary">Gestione storage</h1>
        <p className="text-sm text-text-secondary mt-1">PDF di visure salvati sul server. Cleanup manuale con conferma a doppio click.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <Stat label="File totali" value={data?.count ?? "—"} testId="stat-storage-count" />
        <Stat label="Spazio occupato" value={data ? `${data.total_mb} MB` : "—"} testId="stat-storage-mb" />
        <Stat label="Operazioni cleanup" value={logs.length} sub="storico audit" testId="stat-cleanup-count" />
      </div>

      {/* Cleanup card */}
      <Card className="bg-surface border border-border rounded-xl shadow-sm p-8" data-testid="cleanup-card">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-danger-subtle flex items-center justify-center">
            <Trash2 size={18} className="text-danger" />
          </div>
          <div>
            <h2 className="text-lg font-medium text-text-primary">Elimina PDF precedenti a…</h2>
            <p className="text-sm text-text-secondary mt-0.5">
              Tutti i PDF di richieste create <span className="font-medium">prima</span> della data selezionata verranno eliminati definitivamente. Operazione irreversibile.
            </p>
          </div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Data di taglio</Label>
            <DateInputIT testId="cleanup-date"
              value={beforeDate} onChange={setBeforeDate}
              className="h-11 rounded-lg w-52" />
          </div>
          <Button onClick={openStep1} disabled={!beforeDate}
            data-testid="cleanup-trigger-button"
            className="rounded-lg bg-brand hover:bg-brand-hover text-white h-11 px-5 font-medium">
            <Trash2 size={16} className="mr-1.5" /> Avvia eliminazione
          </Button>
        </div>
      </Card>

      {/* File list */}
      <Card className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <FileText size={16} className="text-text-muted" />
          <h3 className="text-sm font-medium text-text-primary">PDF salvati</h3>
          <span className="text-xs text-text-muted ml-auto">{data?.count ?? 0} file</span>
        </div>
        <Table data-testid="storage-files-table">
          <TableHeader>
            <TableRow className="bg-subtle hover:bg-subtle">
              <TableHead>Data richiesta</TableHead>
              <TableHead>Servizio</TableHead>
              <TableHead>Utente</TableHead>
              <TableHead>File</TableHead>
              <TableHead className="text-right">Dimensione</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-text-muted py-8 text-sm">Caricamento…</TableCell></TableRow>
            ) : failed ? (
              <TableRow><TableCell colSpan={5}><LoadErrorBox what="i PDF salvati" onRetry={load} testId="storage-load-error" /></TableCell></TableRow>
            ) : (data?.files?.length === 0) ? (
              <TableRow><TableCell colSpan={5} className="text-center text-text-muted py-12 text-sm">Nessun PDF salvato</TableCell></TableRow>
            ) : data?.files?.map((f) => (
              <TableRow key={f.request_id} data-testid={`storage-row-${f.request_id}`}>
                <TableCell className="text-sm text-text-secondary whitespace-nowrap">
                  {f.request_created_at ? new Date(f.request_created_at).toLocaleString("it-IT") : "—"}
                </TableCell>
                <TableCell className="text-sm text-text-primary">{f.service_name || "—"}</TableCell>
                <TableCell className="text-sm text-text-secondary">{f.user_email || "—"}</TableCell>
                <TableCell className="text-xs text-text-secondary font-mono truncate max-w-xs">{f.filename}</TableCell>
                <TableCell className="text-right text-sm text-text-primary">{(f.size_bytes / 1024).toFixed(0)} KB</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Audit log */}
      <Card className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <HistoryIcon size={16} className="text-text-muted" />
          <h3 className="text-sm font-medium text-text-primary">Storico cleanup</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-subtle hover:bg-subtle">
              <TableHead>Data esecuzione</TableHead>
              <TableHead>Admin</TableHead>
              <TableHead>Cutoff</TableHead>
              <TableHead className="text-right">File eliminati</TableHead>
              <TableHead className="text-right">MB liberati</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-text-muted py-8 text-sm">Nessuna operazione di cleanup eseguita</TableCell></TableRow>
            ) : logs.map((l) => (
              <TableRow key={l.id} data-testid={`cleanup-log-${l.id}`}>
                <TableCell className="text-sm text-text-secondary whitespace-nowrap">{new Date(l.executed_at).toLocaleString("it-IT")}</TableCell>
                <TableCell className="text-sm text-text-primary">{l.admin_email}</TableCell>
                <TableCell className="text-sm text-text-secondary">{l.before_date}</TableCell>
                <TableCell className="text-right text-sm text-text-primary">{l.files_deleted}</TableCell>
                <TableCell className="text-right text-sm text-text-primary">{(l.bytes_freed / 1024 / 1024).toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* STEP 1: prima conferma */}
      <Dialog open={step1Open} onOpenChange={setStep1Open}>
        <DialogContent className="rounded-xl" data-testid="cleanup-confirm-step1">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium flex items-center gap-2">
              <AlertTriangle size={18} className="text-warning" /> Conferma eliminazione
            </DialogTitle>
            <DialogDescription className="text-sm text-text-secondary leading-relaxed pt-2">
              Stai per eliminare <strong>{filesPreviewCount}</strong> file PDF di richieste create prima del <strong>{beforeDate}</strong>.
              <br />Questa azione è <strong>irreversibile</strong>: i PDF non potranno essere recuperati.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep1Open(false)} className="rounded-lg">Annulla</Button>
            <Button onClick={proceedToStep2} data-testid="cleanup-step1-confirm"
              className="rounded-lg bg-warning-subtle0 hover:bg-amber-600 text-white">
              Procedi alla conferma finale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* STEP 2: doppia conferma con checkbox */}
      <Dialog open={step2Open} onOpenChange={(o) => { setStep2Open(o); if (!o) setAcknowledged(false); }}>
        <DialogContent className="rounded-xl" data-testid="cleanup-confirm-step2">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium text-danger-strong flex items-center gap-2">
              <Trash2 size={18} /> Eliminazione definitiva
            </DialogTitle>
            <DialogDescription className="text-sm text-text-primary leading-relaxed pt-2">
              <div className="bg-danger-subtle border border-danger-subtle rounded-lg p-4 my-3">
                <div className="font-medium text-rose-900 mb-1">Verranno eliminati definitivamente:</div>
                <ul className="text-rose-800 text-sm space-y-0.5 list-disc list-inside">
                  <li><strong>{filesPreviewCount}</strong> file PDF dal disco</li>
                  <li>Riferimenti PDF nelle richieste corrispondenti (i log resteranno intatti)</li>
                  <li>Cutoff: tutto ciò che è precedente al <strong>{beforeDate}</strong></li>
                </ul>
              </div>
              <label className="flex items-start gap-2 cursor-pointer mt-3">
                <input
                  type="checkbox"
                  data-testid="cleanup-acknowledge-checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-text-primary">
                  Sono consapevole che l'azione è <strong>irreversibile</strong> e i PDF eliminati non potranno essere recuperati. Confermo di voler procedere.
                </span>
              </label>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep2Open(false)} disabled={deleting} className="rounded-lg">Annulla</Button>
            <Button onClick={doDelete} disabled={!acknowledged || deleting}
              data-testid="cleanup-final-confirm"
              className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50">
              {deleting ? "Eliminazione…" : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
